import { captureException } from '../../utils/sentry/sentry';
import { useAudioPlayer } from 'expo-audio';
import { useEffect, useRef } from 'react';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const RINGTONE = require('../../assets/sounds/ringtone.mp3');

export const useRingtone = (shouldRing : boolean) => {

  // useAudioPlayer owns the native player's lifecycle: it loads the source and
  // releases it on unmount, so the explicit load/unload dance the expo-av version
  // needed (and its unused `new Audio.Sound()` placeholder) is gone.
  const player = useAudioPlayer(RINGTONE);
  const ringtoneIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const playSound = async () => {
    try {
      if (shouldRing && player.isLoaded) {
        // expo-av replayAsync() == restart from the beginning
        await player.seekTo(0);
        player.play();
      }
    } catch(error) {
      captureException(error);
    }
  };

  const stopSound = async () => {
    try {
      if (player.isLoaded) {
        // expo-av stopAsync() == pause and rewind
        player.pause();
        await player.seekTo(0);
      }
    } catch(error) {
      captureException(error);
    }
  };

  const cleanUpInterval = () => {
    try {
      if (ringtoneIntervalRef.current) {
        clearInterval(ringtoneIntervalRef.current);
        ringtoneIntervalRef.current = null;
      }
    } catch(error) {
      captureException(error);
    }
  };

  const cleanup = async () => {
    await stopSound();
    cleanUpInterval();
  };

  useEffect(() => {
    ringtoneIntervalRef.current = setInterval(() => {
      playSound();
    }, 7000);

    return () => {
      cleanUpInterval();
    };
  }, [shouldRing]);

  useEffect(() => {
    return () => {
      // Interval only. useAudioPlayer registers its own release effect BEFORE this
      // component's effects, so on unmount the native player is already gone by the
      // time this cleanup runs -- touching player.isLoaded/pause() here threw and
      // shipped a captureException to Sentry on every video-conference teardown.
      // `cleanup` (returned below) stays for callers stopping the ringtone while
      // still mounted.
      cleanUpInterval();
    };
  }, []);

  return cleanup;
};
